import { useEffect, useState } from "react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { CalendarDays, Download, ExternalLink, PlayCircle, Users } from "lucide-react";
import { MentoringTab } from "@/components/MentoringTab";
export default function Dashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const { format } = useCurrency();
  useEffect(() => {
    api.get("/dashboard/me").then((r) => setData(r.data)).catch(() => setError("Couldn't load your dashboard. Please refresh."));
  }, []);
  if (error) return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Navbar variant="adult" />
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <div className="font-adult-heading text-2xl">{error}</div>
        <button data-testid="dashboard-retry" onClick={() => window.location.reload()} className="mt-4 rounded-full border border-zinc-800 hover:border-zinc-600 px-5 py-2 text-sm">Retry</button>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-[#09090B] text-zinc-400 flex items-center justify-center">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Navbar variant="adult" />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 font-adult-mono mb-3">Welcome back, {user?.name?.split(" ")[0]}</div>
        <h1 className="font-adult-heading text-4xl md:text-5xl font-semibold tracking-tight">Your learning, in one place.</h1>

        <Tabs defaultValue="sessions" className="mt-10">
          <TabsList data-testid="dashboard-tabs" className="bg-zinc-900 border border-zinc-800 flex-wrap h-auto">
            <TabsTrigger value="sessions" data-testid="tab-sessions">Live Sessions</TabsTrigger>
            <TabsTrigger value="mentoring" data-testid="tab-mentoring">1-on-1 &amp; Workshops</TabsTrigger>
            <TabsTrigger value="courses" data-testid="tab-courses">My Courses</TabsTrigger>
            <TabsTrigger value="kids" data-testid="tab-kids">Kids Packs</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
          </TabsList>

          <TabsContent value="sessions" className="mt-6">
            {data.live_sessions.length === 0 ? (
              <EmptyState msg="No flagship bookings yet. Join the next weekend batch." ctaTo="/flagship" ctaLabel="View bootcamp" />
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {data.live_sessions.map((s, i) => (
                  <div key={i} className="rounded-2xl border border-zinc-800 p-6">
                    <div className="text-xs uppercase tracking-[0.2em] text-amber-400 font-adult-mono">Flagship · {s.cohort?.name}</div>
                    <div className="mt-2 font-adult-heading text-xl">{s.program?.title}</div>
                    <div className="mt-3 text-sm text-zinc-400 flex items-center gap-2"><CalendarDays className="w-4 h-4"/>{new Date(s.cohort?.start_date_iso).toLocaleString(undefined,{dateStyle:"full", timeStyle:"short"})}</div>
                    <div className="mt-1 text-sm text-zinc-400">{s.cohort?.days}</div>
                    <a href={s.cohort?.meeting_link || "#"} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm">
                      Join meeting <ExternalLink className="w-4 h-4"/>
                    </a>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="mentoring" className="mt-6">
            <MentoringTab oneOnOnes={data.one_on_ones} workshops={data.workshops} />
          </TabsContent>

          <TabsContent value="courses" className="mt-6">
            {data.courses.length === 0 ? (
              <EmptyState msg="You haven't enrolled in a mini course yet." ctaTo="/courses" ctaLabel="Explore courses" />
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {data.courses.map((c) => (
                  <Link key={c.course_id} to={`/courses/${c.course_id}`} className="rounded-2xl border border-zinc-800 p-6 hover:border-amber-500/60 transition-smooth">
                    <div className="flex items-start gap-4">
                      <img src={c.thumbnail} alt="" className="w-24 h-24 object-cover rounded-lg border border-zinc-800"/>
                      <div className="flex-1 min-w-0">
                        <div className="font-adult-heading text-xl">{c.title}</div>
                        <div className="mt-3 text-xs text-zinc-500 font-adult-mono uppercase tracking-[0.2em]">Progress · {c.progress_pct}%</div>
                        <Progress value={c.progress_pct} className="h-1.5 mt-2 bg-zinc-900" />
                        <div className="mt-3 text-sm text-amber-400 inline-flex items-center gap-1"><PlayCircle className="w-4 h-4"/>Continue learning</div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="kids" className="mt-6">
            {/* Upcoming scheduled kids sessions (added by admin) */}
            {data.kids_upcoming?.length > 0 && (
              <div className="mb-6">
                <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 font-adult-mono mb-3">Next up · scheduled by admin</div>
                <div className="grid md:grid-cols-2 gap-3">
                  {data.kids_upcoming.map((e) => (
                    <div key={e.event_id} data-testid={`kids-upcoming-${e.event_id}`} className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-amber-400 font-adult-mono">{e.subject}{e.age_tier ? ` · Ages ${e.age_tier}` : ""}</div>
                      <div className="mt-1 font-adult-heading text-lg">{e.title}</div>
                      <div className="mt-2 text-sm text-zinc-400 flex items-center gap-2"><CalendarDays className="w-4 h-4"/>{new Date(e.start_time_iso).toLocaleString(undefined, {dateStyle:"medium", timeStyle:"short"})} · {e.duration_min}min</div>
                      {e.description && <div className="mt-2 text-xs text-zinc-500 line-clamp-2">{e.description}</div>}
                      {e.meeting_link && <a href={e.meeting_link} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs text-amber-400 hover:text-amber-300"><ExternalLink className="w-3.5 h-3.5"/>Join link</a>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {data.kids_sessions.length === 0 ? (
              <EmptyState msg="No kids packs yet." ctaTo="/kids" ctaLabel="Browse kids programs" />
            ) : (
              <>
                <div className="text-[11px] uppercase tracking-[0.28em] text-zinc-500 font-adult-mono mb-3">Purchased packs</div>
                <div className="grid md:grid-cols-3 gap-4">
                  {data.kids_sessions.map((k, i) => (
                    <div key={i} className="rounded-2xl border border-zinc-800 p-6">
                      <div className="text-xs uppercase tracking-[0.2em] text-amber-400 font-adult-mono">{k.subject}</div>
                      <div className="mt-2 font-adult-heading text-xl">{k.title}</div>
                      <div className="mt-3 text-sm text-zinc-400 flex items-center gap-2"><Users className="w-4 h-4"/>{k.sessions_purchased} sessions</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="invoices" className="mt-6">
            {data.invoices.length === 0 ? (
              <EmptyState msg="No purchases yet." />
            ) : (
              <div className="rounded-2xl border border-zinc-800 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase tracking-[0.2em] font-adult-mono">
                    <tr><th className="text-left p-4">Date</th><th className="text-left p-4">Item</th><th className="text-left p-4">Coupon</th><th className="text-right p-4">Amount</th></tr>
                  </thead>
                  <tbody>
                    {data.invoices.map((i) => (
                      <tr key={i.session_id} className="border-t border-zinc-900">
                        <td className="p-4 text-zinc-400">{new Date(i.created_at).toLocaleDateString()}</td>
                        <td className="p-4">{i.item_kind.replace(/_/g," ")}</td>
                        <td className="p-4 text-zinc-500 font-adult-mono">{i.coupon_code || "—"}</td>
                        <td className="p-4 text-right font-adult-mono">{format(i.amount, i.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      <Footer variant="adult" />
    </div>
  );
}

function EmptyState({ msg, ctaTo, ctaLabel }) {
  return (
    <div className="rounded-2xl border border-dashed border-zinc-800 p-14 text-center">
      <div className="text-zinc-400">{msg}</div>
      {ctaTo && <Link to={ctaTo} className="mt-4 inline-block text-amber-400 hover:text-amber-300 text-sm">{ctaLabel} →</Link>}
    </div>
  );
}
